import {
  deriveAccessibleColors,
  gemVar,
  textContrast,
  ThemedColors,
} from '@opallabs/gem'
import { type CalendarItem } from './types'

export type BarColors = {
  border: string
  /** Reads on a solid fill of the chosen color. */
  contrastForeground: string
  fill: string
  foreground: string
  /** `fill`, mixed stronger, for the bar the pointer is on. */
  hoverFill: string
}

/** The share of the chosen color in a bar's fill, matching the design. */
export const FILL_STRENGTH = '14%'

/**
 * The same share under the pointer (Moment Card, State=Hover). A step a
 * person can see without the bar changing character. A chip does not tint on
 * hover: the design gives it a grey face, so it never reads as a bar.
 */
export const HOVER_FILL_STRENGTH = '20%'

const barColorCache = new Map<string, BarColors | null>()

export const indexItemsById = (
  items: readonly CalendarItem[]
): Map<string, CalendarItem> => new Map(items.map((item) => [item.id, item]))

/**
 * The color an item carries in the label set a person chose to color by, or
 * `undefined` when it carries no label in that set — which is also the answer
 * for every item while no set is chosen.
 */
export type ResolveChosenColor = (item: CalendarItem) => string | undefined

/**
 * Picks the color an item is drawn in.
 *
 * A chosen label set wins, first from the item's own label in that set and then
 * from its parent moment's, so that everything under one moment still reads as
 * one push when the moment alone carries the label.
 *
 * Otherwise content takes its parent moment's color when the moment is loaded.
 * That tether is the point: a person scanning the grid should see a moment's bar
 * and its content wearing the same color. Content without a parent in view falls
 * back to its own color, then to its status color.
 */
export const resolveItemColor = (
  item: CalendarItem,
  itemsById: Map<string, CalendarItem>,
  resolveChosenColor?: ResolveChosenColor
): string | undefined => {
  const parent =
    item.kind === 'content' && item.parentId
      ? itemsById.get(item.parentId)
      : undefined

  const chosen =
    resolveChosenColor === undefined
      ? undefined
      : (resolveChosenColor(item) ??
        (parent === undefined ? undefined : resolveChosenColor(parent)))

  return chosen ?? parent?.primaryColor ?? item.primaryColor ?? item.statusColor
}

/**
 * Derives the fill, border and text colors for a bar tinted with a color a
 * person chose. `deriveAccessibleColors` does the contrast work, so a dark label
 * color stays readable in either theme.
 *
 * Results are cached because a workspace has a handful of colors and a month can
 * have hundreds of bars, and parsing a color is not free.
 */
export const barColors = (color: string): BarColors | null => {
  const cached = barColorCache.get(color)
  if (cached !== undefined) return cached

  let derived: BarColors | null
  try {
    const { border } = deriveAccessibleColors(color)
    const tint = (strength: string) =>
      `color-mix(in oklab, ${color} ${strength}, ${gemVar(ThemedColors.Surface1)})`

    derived = {
      border,
      contrastForeground: textContrast(color),
      // Mixing into the surface rather than into transparency keeps the tint
      // stable when a bar sits on the dimmed cross-month cells.
      fill: tint(FILL_STRENGTH),
      hoverFill: tint(HOVER_FILL_STRENGTH),
      // `deriveAccessibleColors` pairs its foreground with the util's own
      // saturated background. On a 14% tint the plain text color is the
      // readable one.
      foreground: gemVar(ThemedColors.TextPrimary),
    }
  } catch {
    console.error('Calendar item has an unreadable color, falling back', {
      color,
    })
    derived = null
  }

  barColorCache.set(color, derived)
  return derived
}
